import { useContext } from "react";
import ThemeContext from "../../lib/theme-context";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import markets from "../../lib/markets";

const InstrumentSelect = ({ value, onChange, size = "medium", fullWidth = true, variant = "default" }) => {
  const theme = useContext(ThemeContext);
  const isCompact = variant === "compact";

  // Filter out category headers (Forex, Indices, Commodities) and specific instruments
  const instrumentOptions = markets.filter(
    (m) => m.ticker && m.ticker !== "" && m.ticker !== "AUS200"
  );

  // Group instruments by category
  const groupedOptions = instrumentOptions.map((m) => {
    let category = "";
    const index = markets.findIndex((market) => market.ticker === m.ticker);
    
    // Find the category by looking backwards for the nearest header
    for (let i = index - 1; i >= 0; i--) {
      if (markets[i].ticker === "" && markets[i].label) {
        category = markets[i].label;
        break;
      }
    }
    
    return {
      ...m,
      category,
    };
  });

  return (
    <Autocomplete
      disablePortal
      value={instrumentOptions.find((m) => m.ticker === value) || null}
      onChange={(event, newValue) => {
        onChange({ target: { value: newValue?.ticker || "" } });
      }}
      options={groupedOptions}
      groupBy={(option) => option.category}
      getOptionLabel={(option) => option.label || ""}
      isOptionEqualToValue={(option, value) => option.ticker === value?.ticker}
      filterOptions={(options, { inputValue }) => {
        const searchValue = inputValue.toLowerCase().replace(/\//g, "");
        return options.filter((option) => {
          const label = (option.label || "").toLowerCase().replace(/\//g, "");
          const ticker = (option.ticker || "").toLowerCase().replace(/\//g, "");
          return label.includes(searchValue) || ticker.includes(searchValue);
        });
      }}
      size={size}
      fullWidth={fullWidth}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder="Instrument"
          InputProps={{
            ...params.InputProps,
            style: {
              color: value ? (theme[0] === "dark" ? "#FCFCFF" : "#1f2937") : (theme[0] === "dark" ? "#9ca3af" : "#6b7280"),
              fontSize: value ? (isCompact ? "13px" : "14px") : (isCompact ? "13px" : "16px"),
              fontWeight: 600,
            },
          }}
          sx={{
            "& .MuiInputLabel-root": {
              color: "#FCFCFF !important",
            },
            "& .MuiOutlinedInput-root": {
              backgroundColor: theme[0] === "dark" ? "#3a3d45" : "#e5e7eb",
              color: theme[0] === "dark" ? "#FCFCFF" : "#1f2937",
              borderRadius: "12px",
              fontSize: isCompact ? "13px" : "16px",
              fontWeight: 600,
              minHeight: isCompact ? "34px" : "39.5px",
              paddingLeft: isCompact ? "10px" : "12px",
              paddingRight: isCompact ? "10px" : "12px",
              paddingTop: "0",
              paddingBottom: "0",
              display: "flex",
              alignItems: "center",
              "& input": {
                color: theme[0] === "dark" ? "#FCFCFF" : "#1f2937",
                padding: "0 !important",
                paddingLeft: "6px !important",
                "&::placeholder": {
                  color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                  opacity: 1,
                },
              },
              "& fieldset": {
                border: "none",
              },
              "&:hover": {
                backgroundColor: theme[0] === "dark" ? "#434650" : "#d1d5db",
              },
              "&:hover fieldset": {
                border: "none",
              },
              "&.Mui-focused": {
                backgroundColor: theme[0] === "dark" ? "#3a3d45" : "#e5e7eb",
              },
              "&.Mui-focused fieldset": {
                border: "none",
              },
            },
            "& .MuiSvgIcon-root": {
              fill: theme[0] === "dark" ? "#FCFCFF" : "#1f2937",
            },
            "& .MuiAutocomplete-clearIndicator": {
              color: theme[0] === "dark" ? "#FCFCFF" : "#1f2937",
            },
            "& .MuiAutocomplete-endAdornment": {
              "& .MuiSvgIcon-root": {
                fill: theme[0] === "dark" ? "#FCFCFF" : "#1f2937",
              },
            },
          }}
        />
      )}
      slotProps={{
        paper: {
          sx: {
            zIndex: 99999,
            bgcolor: theme[0] === "dark" ? "rgba(40, 42, 46)" : "#FCFCFF",
            "& .MuiAutocomplete-listbox": {
              bgcolor: theme[0] === "dark" ? "rgba(40, 42, 46)" : "#FCFCFF",
              maxHeight: "450px",
              padding: 0,
              "& .MuiAutocomplete-option": {
                color: theme[0] === "dark" ? "#FCFCFF" : "black",
                backgroundColor: theme[0] === "dark" ? "rgba(40, 42, 46)" : "#FCFCFF",
                padding: "12px",
                justifyContent: "center",
                minHeight: "auto",
                "&:hover": {
                  backgroundColor: theme[0] === "dark" ? "rgba(252, 252, 255, 0.1)" : "rgba(0, 0, 0, 0.04)",
                },
                "&[aria-selected='true']": {
                  bgcolor:
                    theme[0] === "dark"
                      ? "rgba(255, 255, 255, 0.16)"
                      : "rgba(0, 0, 0, 0.08)",
                },
                "&.Mui-focused": {
                  bgcolor:
                    theme[0] === "dark"
                      ? "rgba(255, 255, 255, 0.08)"
                      : "rgba(0, 0, 0, 0.04)",
                },
              },
              "& .MuiAutocomplete-groupLabel": {
                bgcolor: theme[0] === "dark" ? "rgba(40, 42, 46)" : "#FCFCFF",
                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                fontSize: "13px",
                textAlign: "center",
                position: "sticky",
                top: "-8px",
                padding: "8px 16px",
                fontWeight: 600,
              },
            },
            "& .MuiAutocomplete-noOptions": {
              color: theme[0] === "dark" ? "#FCFCFF" : "black",
              backgroundColor: theme[0] === "dark" ? "rgba(40, 42, 46)" : "#FCFCFF",
              padding: "12px",
              textAlign: "center",
            },
            "& li[data-value='no options']": {
              color: theme[0] === "dark" ? "#FCFCFF" : "black",
              backgroundColor: theme[0] === "dark" ? "rgba(40, 42, 46)" : "#FCFCFF",
            },
          },
        },
      }}
    />
  );
};

export default InstrumentSelect;
